var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var OrbitPlannerCamera_1;
import { IVector3, LineType, IMatrix4, IBoundingBox } from '../../api.js';
import { RAD2DEG, DEG2RAD, EPSILON_DISTANCE_SQUARED, clamp, lerp, EPSILON_DISTANCE, EPSILON_ANGLE_DEGREES, EPSILON_ANGLE_RADIANS } from '../../Math.js';
import { update_from_partial } from '../../babylon/utility/utils.js';
import { FieldOfViewDirection, PerspectiveProjection } from '../PerspectiveProjection.js';
import { ViewerCamera } from '../../babylon/camera/ViewerCamera.js';
import { Observable } from '../../Observable.js';
import { PrimitiveBuilder } from '../PrimitiveBuilder/PrimitiveBuilder.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
import { ORBIT_PLANNER_CAMERA_DESCRIPTION } from './debug/OrbitPlannerCameraDescription.js';
import { OrbitPlannerCameraVisualizer } from './debug/visualizers/OrbitPlannerCameraVisualizer.js';
import { create_visualizers_container } from './debug/visualizers/VisualizersContainer.js';
import { CameraFrustumVisualizer } from '../../babylon/debug/visualizers/CameraFrustumVisualizer.js';
import { Deprecated } from '../../utils/Deprecated.js';
import { Logger } from '../../logging/Logger.js';
const LOGGER_CATEGORY = 'OrbitPlannerCamera';
const MAX_PITCH = Math.PI / 2 - 0.01;
const DEFAULT_PAN_SENSITIVITY_RADIUS = 1;
const DEFAULT_OFFSET = { min: 0.5, max: 40 };
const ALLOWED_OFFSET = { min: 0.1, max: Infinity };
const DEFAULT_PLANE = { near: 0.1, far: 100 };
const DEBOUNCE_TIME = 0.3;
const INERTIA_SPEED_YAW_PITCH = 10;
const INERTIA_SPEED_PITCH_COLLISION = 30;
const INERTIA_SPEED_ZOOM = 10;
const INERTIA_SPEED_PAN = 10;
const ZOOM_DELTA_PERCENTAGE = 0.1;
const LIMIT_SPRING_STIFFNESS_YAW_PITCH = 8;
const LIMIT_SPRING_STIFFNESS_PAN = 5.0;
const LIMIT_SPRING_LERP_MULTIPLIER = 4.0;
function target_lerp(value, target, t, epsilon) {
    if (Math.abs(value - target) <= epsilon)
        return target;
    return lerp(value, target, t);
}
function target_lerp_vec3(value, target, t, epsilon) {
    if (IVector3.distance_squared(value, target) <= epsilon * epsilon) {
        return IVector3.copy(target);
    }
    else {
        return IVector3.lerp(value, target, t);
    }
}
let OrbitPlannerCamera = OrbitPlannerCamera_1 = class OrbitPlannerCamera extends ViewerCamera {
    drag_start(evt) {
        this._is_dragging = true;
    }
    drag_end(evt) {
        this._is_dragging = false;
    }
    pan_start(evt) {
        this._is_panning = true;
    }
    pan_end(evt) {
        this._is_panning = false;
    }
    update_aspect_ratio(aspect_ratio) {
        this._projection.aspect_ratio = aspect_ratio;
    }
    constructor(position, target, viewer, field_of_view_direction = FieldOfViewDirection.HORIZONTAL) {
        super(viewer);
        this._pivot = new IVector3(0, 0, 0);
        this._target_pivot = new IVector3(0, 0, 0);
        this._yaw = 0;
        this._target_yaw = 0; // Used for inertia
        this._pitch = 0;
        this._target_pitch = 0; // Used for inertia
        this._offset = 3;
        this._target_offset = 3; // Used for inertia
        this._yaw_min = Number.NEGATIVE_INFINITY;
        this._yaw_max = Number.POSITIVE_INFINITY;
        this._pitch_min = -MAX_PITCH;
        this._pitch_max = MAX_PITCH;
        this._offset_min = DEFAULT_OFFSET.min;
        this._offset_max = DEFAULT_OFFSET.max;
        this._pan_min = new IVector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
        this._pan_max = new IVector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        this._internal_camera_transform = new IMatrix4();
        this._view_matrix_cache = new IMatrix4();
        this._is_dragging = false;
        this._is_panning = false;
        this._debug_geometry = null;
        this.custom_world_bounds = null;
        this._is_dirty = true;
        this._notify_observers = false;
        this._input_debounce_timer = 0;
        this._pan_input = new IVector3(0, 0, 0);
        this._pitch_input = 0.0;
        this._yaw_input = 0.0;
        this._zoom_input = 0.0;
        // The frame count for which this camera was last updated
        this._previous_update_frame = -1;
        this.pivot_position_change_observable = new Observable();
        this.name = 'OrbitPlannerCamera';
        this._projection = new PerspectiveProjection(Math.PI / 4, 2, DEFAULT_PLANE.near, DEFAULT_PLANE.far, field_of_view_direction);
        this.look_at(position, target);
    }
    /**
     * Clones the camera to create an exact duplicate.
     * @returns A new Camera instance that is an exact copy of the original camera.
     */
    clone() {
        const clone = new OrbitPlannerCamera_1(this.position, this.target, this.viewer);
        clone._projection = this._projection.clone();
        // Limits
        IVector3.copy_to_ref(this._pan_min, clone._pan_min);
        IVector3.copy_to_ref(this._pan_max, clone._pan_max);
        clone._offset_max = this._offset_max;
        clone._offset_min = this._offset_min;
        clone._yaw_max = this._yaw_max;
        clone._yaw_min = this._yaw_min;
        clone._pitch_max = this._pitch_max;
        clone._pitch_min = this._pitch_min;
        return clone;
    }
    /**
     * Gets the rotation along the ground plane
     */
    get yaw_radians() {
        return this._yaw;
    }
    /**
     * Sets the rotation along the ground plane.
     * Clears any limits that have been set.
     */
    set yaw_radians(val) {
        this.clear_all_limits();
        this.limited_yaw_radians = this.limit_yaw(val);
        this.reset_inertia();
    }
    set limited_yaw_radians(val) {
        if (this._yaw === val)
            return;
        this._yaw = val;
        this._is_dirty = true;
    }
    /**
     * Gets the rotation along the ground plane
     */
    get yaw_degrees() {
        return this.yaw_radians * RAD2DEG;
    }
    /**
     * Sets the rotation along the ground plane.
     * Clears any limits that have been set.
     */
    set yaw_degrees(val) {
        this.yaw_radians = val * DEG2RAD;
    }
    /**
     * Gets the up / down rotation of the camera
     */
    get pitch_radians() {
        return this._pitch;
    }
    /**
     * Sets the up / down rotation of the camera.
     * Clears any limits that have been set.
     */
    set pitch_radians(val) {
        this.clear_all_limits();
        this.limited_pitch_radians = this.limit_pitch(val);
        this.reset_inertia();
    }
    set limited_pitch_radians(val) {
        if (this._pitch === val)
            return;
        this._pitch = val;
        this._is_dirty = true;
    }
    /**
     * Gets the up / down rotation of the camera
     */
    get pitch_degrees() {
        return this.pitch_radians * RAD2DEG;
    }
    /**
     * Sets the up / down rotation of the camera.
     * Clears any limits that have been set.
     */
    set pitch_degrees(val) {
        this.pitch_radians = val * DEG2RAD;
    }
    /**
     * Sets the up / down rotation of the camera while respecting current limits
     */
    set limited_pitch_degrees(val) {
        this.limited_pitch_radians = val * DEG2RAD;
    }
    /**
     * Gets the distance from the pivot/target to the cameras position
     */
    get offset() {
        return this._offset;
    }
    /**
     * Sets the distance from the pivot/target to the cameras position.
     * Clears any limits that have been set.
     */
    set offset(val) {
        this.clear_all_limits();
        this.limited_offset = this.limit_offset(val);
        this.reset_inertia();
    }
    /**
     * Sets the distance from the pivot/target to the cameras position respecting current limits
     */
    set limited_offset(val) {
        if (this._offset !== val) {
            this._offset = val;
            this._is_dirty = true;
        }
    }
    /**
     * Gets a copy of the oribiting position
     */
    get pivot() {
        return IVector3.copy(this._pivot);
    }
    /**
     * Sets the orbiting position.
     * Clears any limits that have been set.
     */
    set pivot(val) {
        this.clear_all_limits();
        this.limited_pivot = this.limit_pivot_to_ref(val, new IVector3());
        this.reset_inertia();
    }
    set limited_pivot(val) {
        if (!IVector3.equals(this._pivot, val)) {
            IVector3.copy_to_ref(val, this._pivot);
            this._is_dirty = true;
            this.pivot_position_change_observable.notify(this, this._pivot); // For updating debug geometry, and similar.
        }
    }
    /**
     * Gets a copy of the oribiting position, alias for pivot
     */
    get target() {
        return this.pivot;
    }
    /**
     * Sets the orbiting position, alias for pivot.
     * Clears any limits that have been set.
     */
    set target(pos) {
        this.pivot = pos;
    }
    get blend_target() {
        return this._pivot;
    }
    get projection() {
        // Make sure our projection is up to date as we automatically calculate near/far, which depends on viewing angle, scene bounds, etc.
        this.update_projection_near_far();
        return this._projection;
    }
    // ------------------------------
    // --------- Projection ---------
    // ------------------------------
    /**
     * Gets the horizontal field of view of the camera
     */
    get field_of_view_degrees() {
        return this._projection.field_of_view * RAD2DEG;
    }
    /**
     * Sets the horizontal field of view of the camera
     */
    set field_of_view_degrees(fov_degrees) {
        this._projection.field_of_view = fov_degrees * DEG2RAD;
    }
    /**
     * Gets the horizontal field of view of the camera
     */
    get field_of_view_radians() {
        return this._projection.field_of_view;
    }
    /**
     * Sets the horizontal field of view of the camera
     */
    set field_of_view_radians(fov_radians) {
        this._projection.field_of_view = fov_radians;
    }
    /**
     * Used to limit the yaw value based on yaw_min and yaw_max
     * @param yaw The value to limit
     */
    limit_yaw(yaw) {
        return clamp(yaw, this._yaw_min, this._yaw_max);
    }
    /**
     * Used to limit the pitch value based on pitch_min and pitch_max
     * @param pitch The value to limit
     */
    limit_pitch(pitch) {
        return clamp(pitch, this._pitch_min, this._pitch_max);
    }
    /**
     * Used to limit the offset (zoom) value based on offset_min and offset_max
     * @param offset The value to limit
     */
    limit_offset(offset) {
        return clamp(offset, this._offset_min, this._offset_max);
    }
    /**
     * Used to limit the pivot value based on pivot_min and pivot_max
     * @param pivot The vector to limit
     * @param result The Babylon vector which will contain the result
     */
    limit_pivot_to_ref(pivot, result) {
        return IVector3.set(result, clamp(pivot.x, this._pan_min.x, this._pan_max.x), clamp(pivot.y, this._pan_min.y, this._pan_max.y), clamp(pivot.z, this._pan_min.z, this._pan_max.z));
    }
    /**
     * Called whenever InputManager decided that the camera is the received of a Drag action
     * This will update the target values for yaw, pitch and offset (zoom)
     * @param evt Event with the drag info
     */
    drag_input(evt) {
        this.reset_input_debounce();
        // full screen drag rotates one full turn
        const full_turn = Math.PI * 2;
        this._pitch_input += -evt.delta_y * full_turn;
        this._yaw_input += evt.delta_x * full_turn * this._projection.aspect_ratio;
    }
    pan_input(evt) {
        this.reset_input_debounce();
        const sensitivity_factor = DEFAULT_PAN_SENSITIVITY_RADIUS * this._offset;
        IVector3.add_in_place(this._pan_input, IVector3.scale(this.up, evt.delta_y * sensitivity_factor / this._projection.aspect_ratio));
        IVector3.add_in_place(this._pan_input, IVector3.scale(this.right, -evt.delta_x * sensitivity_factor));
    }
    /**
     * Called whenever the InputManager decided that the camera is the receiver of a zoom action (scroll or pinch)
     * Updates the target value for the offset
     * @param evt Event with the zoom info
     */
    zoom_input(evt) {
        this.reset_input_debounce();
        const offset = Math.max(this._offset, 1);
        const zoom_value = evt.delta * 0.01 * offset * ZOOM_DELTA_PERCENTAGE;
        this._zoom_input += zoom_value;
    }
    get_ground_collision_radius() {
        return Math.max(0.1, 2.0 * PerspectiveProjection.ALLOWED_PLANE.near);
    }
    get_ground_collision_pitch(offset, pivot) {
        const radius = this.get_ground_collision_radius();
        return Math.asin(clamp((pivot.y - radius) / offset, -1.0, 1.0));
    }
    fix_ground(delta_time_in_seconds) {
        // Make sure to do collision math on data where the users inputs have been limited to their constraints.
        const limited_forward = new IVector3();
        const limited_right = new IVector3();
        const limited_up = new IVector3();
        OrbitPlannerCamera_1.calculate_basis_vectors(this.limit_yaw(this._target_yaw), this.limit_pitch(this._target_pitch), limited_right, limited_up, limited_forward);
        const limited_pivot = this.limit_pivot_to_ref(this._target_pivot, new IVector3());
        const limited_offset = this.limit_offset(this._target_offset);
        const limited_pitch = this.limit_pitch(this._target_pitch);
        const position = IVector3.add(limited_pivot, IVector3.scale(limited_forward, -limited_offset));
        const radius = this.get_ground_collision_radius();
        if (position.y < radius) {
            // Calculate the actual pitch needed with our new offset, to not cross the ground plane.
            const collide_pitch = Math.asin(clamp((limited_pivot.y - radius) / limited_offset, -1.0, 1.0));
            if (collide_pitch < limited_pitch) {
                const limited_collide_pitch = this.limit_pitch(collide_pitch);
                if (!this._is_dragging) {
                    this._target_pitch = lerp(this._target_pitch, limited_collide_pitch, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_PITCH_COLLISION));
                }
                if (this._is_dragging || limited_collide_pitch !== collide_pitch) {
                    const sin_limited_collide_pitch = Math.sin(limited_collide_pitch);
                    // Avoid NaNs by only doing this operation when there's some angle between us and our target position
                    if (Math.abs(sin_limited_collide_pitch) > Math.sin(EPSILON_ANGLE_DEGREES)) {
                        const new_target_offset = (limited_pivot.y - radius) / sin_limited_collide_pitch;
                        const limited_new_offset = this.limit_offset(new_target_offset);
                        this._target_offset = lerp(this._target_offset, limited_new_offset, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_PITCH_COLLISION));
                    }
                }
            }
        }
    }
    reset_input_debounce() {
        if (this._input_debounce_timer <= 0) {
            this.interaction_begin_observable.notify(this);
        }
        this._input_debounce_timer = DEBOUNCE_TIME;
    }
    static calculate_basis_vectors(yaw, pitch, right_out, up_out, forward_out) {
        const viewer_camera_basis = new IMatrix4();
        IVector3.copy_to_ref(ViewerCamera.RIGHT, viewer_camera_basis[0]);
        IVector3.copy_to_ref(ViewerCamera.UP, viewer_camera_basis[1]);
        IVector3.copy_to_ref(ViewerCamera.FORWARD, viewer_camera_basis[2]);
        const yaw_rotation = IMatrix4.Rotation_Y(-yaw);
        const pitch_rotation = IMatrix4.Rotation_X(pitch);
        const result = IMatrix4.multiply(IMatrix4.multiply(viewer_camera_basis, yaw_rotation), pitch_rotation);
        if (right_out !== null)
            IVector3.copy_to_ref(result[0], right_out);
        if (up_out !== null)
            IVector3.copy_to_ref(result[1], up_out);
        if (forward_out !== null)
            IVector3.copy_to_ref(result[2], forward_out);
    }
    update_projection_near_far() {
        var _a;
        // Projected distance from the camera forward to the scene sphere bounds
        const scene_bounding_sphere = (_a = this.custom_world_bounds) !== null && _a !== void 0 ? _a : IBoundingBox.to_bounding_sphere(this.viewer.get_scene_bounds());
        const distance = IVector3.dot(IVector3.subtract(scene_bounding_sphere.position, this.position), this.forward);
        this._projection.near = Math.max(PerspectiveProjection.ALLOWED_PLANE.near, distance - scene_bounding_sphere.radius);
        this._projection.far = clamp(distance + scene_bounding_sphere.radius, this._projection.near + EPSILON_DISTANCE, PerspectiveProjection.ALLOWED_PLANE.far);
    }
    get camera_transform() {
        this.update_camera_transform_cache();
        return this._internal_camera_transform;
    }
    get view_matrix() {
        this.update_camera_transform_cache();
        return this._view_matrix_cache;
    }
    /**
     * Recalculates and applies the cameras view matrix, direction vectors and projection
     */
    update_camera_transform_cache() {
        if (!this._is_dirty) {
            return; // If yaw, pitch, offset or pivot has not changes, neither has up, forward, right and position
        }
        OrbitPlannerCamera_1.calculate_basis_vectors(this._yaw, this._pitch, this._internal_camera_transform[0], this._internal_camera_transform[1], this._internal_camera_transform[2]);
        IVector3.add_to_ref(this._pivot, IVector3.scale(this._internal_camera_transform[2], -this._offset), this._internal_camera_transform[3]);
        IMatrix4.inverse_to_ref(this._internal_camera_transform, this._view_matrix_cache);
        this._is_dirty = false;
        this._notify_observers = true;
    }
    /**
     * Called by the Viewer camera manager every frame once the camera has been added to the viewer (viewer.push_camera())
     * Updates yaw, pitch, offset (zoom) and pivot of the camera, based on the target value of each and the time since last frame (delta_time).
     * Also makes sure that the camera does not go below the ground, and then updates the actual camera with any changes.
     * @param delta_time The time in miliseconds since the last frame
     */
    update(delta_time) {
        var _a;
        // We only want to debounce/lerp once per frame even if we are the active camera for multiple views
        const delta_time_in_seconds = this._previous_update_frame !== delta_time.elapsed_frames ? delta_time.seconds : 0;
        this._previous_update_frame = delta_time.elapsed_frames;
        this._input_debounce_timer -= delta_time_in_seconds;
        if (this._input_debounce_timer <= 0 && this._input_debounce_timer + delta_time.seconds > 0) {
            this.interaction_end_observable.notify(this);
        }
        const collide_pitch = this.get_ground_collision_pitch(this._target_offset, this._target_pivot);
        this._target_pitch = this.integrate_springy_constrained_input(this._target_pitch, this._pitch_input, LIMIT_SPRING_STIFFNESS_YAW_PITCH, this._pitch_min, Math.min(this._pitch_max, collide_pitch));
        this._target_yaw = this.integrate_springy_constrained_input(this._target_yaw, this._yaw_input, LIMIT_SPRING_STIFFNESS_YAW_PITCH, this._yaw_min, this._yaw_max);
        this._target_pivot.x = this.integrate_springy_constrained_input(this._target_pivot.x, this._pan_input.x, LIMIT_SPRING_STIFFNESS_PAN, this._pan_min.x, this._pan_max.x);
        this._target_pivot.y = this.integrate_springy_constrained_input(this._target_pivot.y, this._pan_input.y, LIMIT_SPRING_STIFFNESS_PAN, this._pan_min.y, this._pan_max.y);
        this._target_pivot.z = this.integrate_springy_constrained_input(this._target_pivot.z, this._pan_input.z, LIMIT_SPRING_STIFFNESS_PAN, this._pan_min.z, this._pan_max.z);
        // Zero the accumulated input
        this._pitch_input = 0.0;
        this._yaw_input = 0;
        IVector3.set(this._pan_input, 0, 0, 0);
        // Apply zoom input. This has no spring effect.
        this._target_offset = (this._target_offset + this._zoom_input);
        this._zoom_input = 0;
        if (this._pitch_min === this._pitch_max) {
            this._target_pitch = this.limit_pitch(this._target_pitch);
        }
        else if (!this._is_dragging) {
            const collide_pitch = this.get_ground_collision_pitch(this.limit_offset(this._target_offset), this.limit_pivot_to_ref(this._target_pivot, new IVector3()));
            this._target_pitch = target_lerp(this._target_pitch, Math.min(collide_pitch, this.limit_pitch(this._target_pitch)), Math.min(1.0, LIMIT_SPRING_LERP_MULTIPLIER * delta_time_in_seconds * INERTIA_SPEED_YAW_PITCH), EPSILON_ANGLE_RADIANS);
        }
        if (this._yaw_min === this._yaw_max) {
            this._target_yaw = this.limit_yaw(this._target_yaw);
        }
        else if (!this._is_dragging) {
            this._target_yaw = target_lerp(this._target_yaw, this.limit_yaw(this._target_yaw), Math.min(1.0, LIMIT_SPRING_LERP_MULTIPLIER * delta_time_in_seconds * INERTIA_SPEED_YAW_PITCH), EPSILON_ANGLE_RADIANS);
        }
        const target_pivot_lerp_t = Math.min(1.0, delta_time_in_seconds * LIMIT_SPRING_LERP_MULTIPLIER * INERTIA_SPEED_PAN);
        const limited_pivot = this.limit_pivot_to_ref(this._target_pivot, new IVector3());
        if (this._pan_min.x === this._pan_max.x) {
            this._target_pivot.x = limited_pivot.x;
        }
        else if (!this._is_panning) {
            this._target_pivot.x = target_lerp(this._target_pivot.x, limited_pivot.x, target_pivot_lerp_t, EPSILON_DISTANCE);
        }
        if (this._pan_min.y === this._pan_max.y) {
            this._target_pivot.y = limited_pivot.y;
        }
        else if (!this._is_panning) {
            this._target_pivot.y = target_lerp(this._target_pivot.y, limited_pivot.y, target_pivot_lerp_t, EPSILON_DISTANCE);
        }
        if (this._pan_min.z === this._pan_max.z) {
            this._target_pivot.z = limited_pivot.z;
        }
        else if (!this._is_panning) {
            this._target_pivot.z = target_lerp(this._target_pivot.z, limited_pivot.z, target_pivot_lerp_t, EPSILON_DISTANCE);
        }
        this._target_offset =
            (this._offset_min === this._offset_max)
                ? this.limit_offset(this._target_offset)
                : target_lerp(this._target_offset, this.limit_offset(this._target_offset), Math.min(1.0, LIMIT_SPRING_LERP_MULTIPLIER * delta_time_in_seconds * INERTIA_SPEED_ZOOM), EPSILON_DISTANCE);
        this.fix_ground(delta_time_in_seconds);
        // target values are limited in the drag_input and zoom_input functions. Here we apply inertia, lerping towards out target value for smooth camera motion.
        // The limited_x property sets dirty if value has changed.
        this.limited_yaw_radians = target_lerp(this._yaw, this._target_yaw, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_YAW_PITCH), EPSILON_ANGLE_RADIANS);
        this.limited_pitch_radians = target_lerp(this._pitch, this._target_pitch, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_YAW_PITCH), EPSILON_ANGLE_RADIANS);
        this.limited_offset = target_lerp(this._offset, this._target_offset, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_ZOOM), EPSILON_DISTANCE); // Zoom
        this.limited_pivot = target_lerp_vec3(this._pivot, this._target_pivot, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_PAN), EPSILON_DISTANCE); // Pan
        if (this._notify_observers) {
            this.update_observable.notify(this);
            this._notify_observers = false;
        }
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.update();
    }
    get debug_geometry() {
        return this._debug_geometry;
    }
    create_debug_geometry() {
        if (this._debug_geometry)
            return;
        this._debug_geometry = create_visualizers_container({
            frustum: new CameraFrustumVisualizer(this, this.viewer, undefined, 0.01, LineType.DASHED),
            camera: new OrbitPlannerCameraVisualizer(this, this.viewer)
        }, this.viewer, true);
    }
    remove_debug_geometry() {
        var _a;
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.dispose();
        this._debug_geometry = null;
    }
    update_debug_geometry() {
        var _a;
        (_a = this.debug_geometry) === null || _a === void 0 ? void 0 : _a.update();
    }
    /**
     * Calculate the cameras parameters so that the camera looks at the target location from the position parameter.
     * Clears any limits that have been set.
     * @param position The position of the camera
     * @param target The target (pivot) position that the camera will orbit around
     * @param up The desired up direction of the camera, this will be recalculated to be orthogonal to forward and right vectors
     */
    look_at(position, target, up) {
        this.clear_all_limits();
        this.look_at_internal(position, target, up);
        this.reset_inertia();
    }
    look_at_internal(position, target, up) {
        this.limited_pivot = this.limit_pivot_to_ref(target, new IVector3());
        // Find yaw
        // Project position to target vector in xz plane, (handle special case if camera is looking up or down)
        // Default forward is { 0, 0, -1 }
        const look = new IVector3(this._pivot.x - position.x, this._pivot.y - position.y, this._pivot.z - position.z);
        const look_length = IVector3.magnitude(look);
        IVector3.scale_in_place(look, 1.0 / look_length);
        const { yaw, pitch } = OrbitPlannerCamera_1.calculate_yaw_and_pitch(look);
        if (isNaN(yaw) || isNaN(pitch) || isNaN(look_length)) {
            Logger.as(LOGGER_CATEGORY).error("Invalid look_at_internal paramters yielding NaN, ignoring.");
        }
        else {
            this.limited_yaw_radians = this.limit_yaw(yaw);
            this.limited_pitch_radians = this.limit_pitch(pitch);
            this.limited_offset = this.limit_offset(look_length);
        }
    }
    /**
     * Note that this function does not normalize forward so it's very important that it receives a normalized vector.
     * @param forward Normalized forward vector for which to calculate yaw and pitch values
     * @returns The calculated Yaw and Pitch values
     */
    static calculate_yaw_and_pitch(forward) {
        const yaw = Math.atan2(forward.x, -forward.z);
        return {
            pitch: Math.asin(forward.y),
            yaw: yaw
        };
    }
    clear_all_limits() {
        this.set_orbit_limits({});
        this.set_pan_limits({
            min: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY, z: Number.NEGATIVE_INFINITY },
            max: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY, z: Number.POSITIVE_INFINITY }
        });
        this.set_zoom_limits({ near: ALLOWED_OFFSET.min, far: ALLOWED_OFFSET.max });
    }
    /*
     * Limits rotating the camera towards left, right, up, and down.
     * If the forward parameter is omitted the current camera forward will be used.
     * Note that calling this function always updates all orbit limits.
     * Omitting parameters set them to default values rather than keeping them.
     */
    set_orbit_limits(limits) {
        limits = update_from_partial({
            left: Number.POSITIVE_INFINITY,
            right: Number.POSITIVE_INFINITY,
            up: Number.POSITIVE_INFINITY,
            down: Number.POSITIVE_INFINITY,
            forward: this.forward
        }, limits);
        if (IVector3.dot(limits.forward, limits.forward) < EPSILON_DISTANCE_SQUARED) {
            Logger.as(LOGGER_CATEGORY).error('Invalid forward parameter with a length of zero');
            return;
        }
        const { yaw, pitch } = OrbitPlannerCamera_1.calculate_yaw_and_pitch(limits.forward);
        // Since the yaw we calculated has a range of 0-2pi, remap our yaw to 0-2pi + 2xpi where x is the number of turns we've taken
        const turn = Math.floor((this._yaw + Math.PI) / (2.0 * Math.PI));
        const yaw_diff = (yaw + turn * 2.0 * Math.PI);
        this._yaw_min = yaw_diff - limits.left;
        this._yaw_max = yaw_diff + limits.right;
        // Clamp pitch so that its always in range -MAX_PITCH and MAX_PITCH.
        this._pitch_min = clamp(pitch - limits.down, -MAX_PITCH, MAX_PITCH);
        this._pitch_max = clamp(pitch + limits.up, -MAX_PITCH, MAX_PITCH);
        this.reset_inertia();
    }
    /**
     * Limits the minimum and maximum distance the camera can be from the target.
     * Note that calling this function will set all omitted parameters to default values
     */
    set_zoom_limits(limits) {
        limits = update_from_partial({
            near: DEFAULT_OFFSET.min,
            far: DEFAULT_OFFSET.max
        }, limits);
        this._offset_min = Math.max(limits.near, ALLOWED_OFFSET.min);
        this._offset_max = Math.min(limits.far, ALLOWED_OFFSET.max);
        this.limited_offset = this.limit_offset(this._offset);
        this.reset_inertia();
    }
    set_pan_limits(limits) {
        IVector3.set(this._pan_min, limits.min.x, limits.min.y, limits.min.z);
        IVector3.set(this._pan_max, limits.max.x, limits.max.y, limits.max.z);
        this.limited_pivot = this.limit_pivot_to_ref(this._pivot, new IVector3());
        this.reset_inertia();
    }
    /**
     * Set all target values to the current values for yaw, pitch, offset and pivot
     */
    reset_inertia() {
        this._target_yaw = this._yaw;
        this._target_pitch = this._pitch;
        this._target_offset = this._offset;
        IVector3.copy_to_ref(this._pivot, this._target_pivot);
    }
    get_framing_up_vector(direction) {
        // We don't allow setting a custom up vector for framing with this camera, as we only allow setting the forward vector and calculating the rest ourselves
        // Technically, it should be enough in the current implementation to just supply (0, 1, 0) as the up vector for the framing function,
        // as we don't allow pure vertical looking angles anyway.
        // But for future proofing, let's calculate it given the functions we have so it can be re-used in the future.
        if (direction !== undefined) {
            const up = new IVector3();
            const { yaw, pitch } = OrbitPlannerCamera_1.calculate_yaw_and_pitch(direction !== null && direction !== void 0 ? direction : this.forward);
            OrbitPlannerCamera_1.calculate_basis_vectors(yaw, pitch, null, up, null);
            return up;
        }
        else {
            return this.up;
        }
    }
    /**
     * Positions the camera so that the given objects are framed.
     * @param objects The objects to frame
     * @param precise Enable to get precise framing (use only if non-precise is insufficient)
     * @param margin The viewport margin (guaranteed space around the objects) in normalized coordinates. Should be in the range 0 - 0.499
     * @param direction The direction to look in. If undefined, the camera's current direction will be used.
     * @param aspect_ratio The aspect ratio to use for framing. If undefined, the camera's last set aspect ratio is used.
     */
    frame_objects(objects_1) {
        return __awaiter(this, arguments, void 0, function* (objects, precise = false, margin = 0, direction, aspect_ratio) {
            const advanced_experimental_viewer = this.viewer;
            let projection = this._projection;
            if (aspect_ratio !== undefined) {
                projection = this._projection.clone();
                projection.aspect_ratio = aspect_ratio;
            }
            const up = this.get_framing_up_vector(direction);
            if (precise) {
                return advanced_experimental_viewer.get_precise_framed_perspective_camera_matrix(objects, projection, direction || this.forward, up, true, 0.0, margin).then((framing) => {
                    this.look_at(framing.camera_matrix[3], framing.framing_target_position, framing.camera_matrix[1]);
                });
            }
            const all_objects = [];
            for (let i = 0; i < objects.length; i++) {
                all_objects.push(objects[i]);
                all_objects.push(...this.viewer.get_children(objects[i], false, false));
            }
            const bounds_promises = [];
            for (let i = 0; i < all_objects.length; i++) {
                const scene_object = advanced_experimental_viewer.get_scene_object_from_object_id(all_objects[i]);
                bounds_promises.push(scene_object.get_world_bounding_box());
            }
            const bounds = yield Promise.all(bounds_promises);
            const framing = advanced_experimental_viewer.get_perspective_framed_bounds(bounds, projection, direction || this.forward, up, null, 0.0, margin);
            this.look_at(framing.camera_matrix[3], framing.framing_target_position, framing.camera_matrix[1]);
        });
    }
    /**
     * Frame a sphere containing an axis-aligned bounding box.
     * @param bounds The box to frame in view.
     * @param direction The direction in which the camera should look. Defaults to the current camera direction
     */
    frame_consistent(bounds, direction, minimum_margin, aspect_ratio) {
        this.clear_all_limits();
        const center = IVector3.scale_in_place(IVector3.add(bounds.min, bounds.max), 0.5);
        const radius = Math.sqrt(Math.max(IVector3.distance_squared(center, bounds.min), IVector3.distance_squared(center, bounds.max)));
        if (aspect_ratio === undefined) {
            aspect_ratio = this._projection.aspect_ratio;
        }
        // https://stackoverflow.com/a/44849975
        const horizontal = aspect_ratio > 1;
        const fov = horizontal ? this._projection.vertical_fov : this._projection.horizontal_fov;
        let pad = 1;
        // Normalize padding values from pixels to NDC
        // Currently per axis, not per side.
        const vertical_pad = 1.0 - (2.0 * (minimum_margin || 0));
        const horizontal_pad = 1.0 - (2.0 * (minimum_margin || 0));
        // Smaller value equals more padding (it's a divider).
        // Choose which padding depending on aspect ratio and bring
        // the other value into the same space.
        pad = horizontal ? Math.min(vertical_pad, horizontal_pad * aspect_ratio) : Math.min(horizontal_pad, vertical_pad / aspect_ratio);
        const distance = radius / pad / Math.sin(fov * 0.5);
        const dir = direction ? IVector3.normalize(direction) : this.forward;
        const position = IVector3.subtract(center, IVector3.scale(dir, distance));
        this.look_at(position, center);
    }
    generate_debug_geometries() {
        const primitive_builder = new PrimitiveBuilder(this.viewer);
        const target_sphere = primitive_builder.create_sphere(0.06, { segments: 64 });
        /**
         * All required lines are
         * 0->1, 0->3, 0->4
         * 2->1, 2->3, 2->6
         * 5->1, 5->4, 5->6
         * 7->3, 7->4, 7->6
         */
        const indices = [
            0, 1, 0, 3, 0, 4,
            2, 1, 2, 3, 2, 6,
            5, 1, 5, 4, 5, 6,
            7, 3, 7, 4, 7, 6
        ];
        const color = { r: 1, g: 0.2, b: 0.2 };
        /**
         *   5_____6
         *   /:    /|
         * 1/_:__2/ |
         *  |4:...|7/
         * 0|/___3|/
         * The following list has the corners depicted in this cube
         */
        const get_pan_limit_corners = () => [
            { x: this._pan_min.x, y: this._pan_min.y, z: this._pan_min.z },
            { x: this._pan_min.x, y: this._pan_max.y, z: this._pan_min.z },
            { x: this._pan_max.x, y: this._pan_max.y, z: this._pan_min.z },
            { x: this._pan_max.x, y: this._pan_min.y, z: this._pan_min.z },
            { x: this._pan_min.x, y: this._pan_min.y, z: this._pan_max.z },
            { x: this._pan_min.x, y: this._pan_max.y, z: this._pan_max.z },
            { x: this._pan_max.x, y: this._pan_max.y, z: this._pan_max.z },
            { x: this._pan_max.x, y: this._pan_min.y, z: this._pan_max.z }
        ];
        const ui_lines = [];
        const corners = get_pan_limit_corners();
        for (let i = 0; i < indices.length; i += 2) {
            ui_lines.push(this.viewer.create_ui_line(LineType.SOLID, corners[indices[i + 0]], corners[indices[i + 1]], color, 0.01));
        }
        this.pivot_position_change_observable.add((sender, pivot) => {
            this.viewer.set_position(target_sphere, pivot.x, pivot.y, pivot.z);
        });
        this.update_observable.add(() => {
            const corners = get_pan_limit_corners();
            let corners_have_infinite_limits = false;
            for (let i = 0; i < corners.length && !corners_have_infinite_limits; i++) {
                corners_have_infinite_limits = !isFinite(corners[i].x) || !isFinite(corners[i].y) || !isFinite(corners[i].z);
            }
            if (corners_have_infinite_limits) {
                for (let i = 0; i < ui_lines.length; i++) {
                    this.viewer.set_ui_line_visible(ui_lines[i], false);
                }
            }
            else {
                for (let i = 0, line_index = 0; i < indices.length; i += 2, line_index++) {
                    const ui_line = ui_lines[line_index];
                    this.viewer.set_ui_line_visible(ui_line, true);
                    this.viewer.update_ui_line(ui_line, corners[indices[i + 0]], corners[indices[i + 1]]);
                }
            }
        });
    }
    dispose_impl() {
        // Do nothing
    }
    get_projection_matrix(_custom_aspect_ratio) {
        if (_custom_aspect_ratio === undefined) {
            return this.projection.get_projection_matrix();
        }
        else {
            const projection = this.projection.clone();
            projection.aspect_ratio = _custom_aspect_ratio;
            return projection.get_projection_matrix();
        }
    }
    /**
     * Helper function to integrate input gathered over a frame, with springy limits.
     * @param x The state of the input
     * @param delta The input delta gathered during the frame
     * @param stiffness The stiffness of the "spring" beyond the limits
     * @param limit_min The limit minimum
     * @param limit_max The limit maximum
     * @returns The new input's state value
     */
    integrate_springy_constrained_input(x, delta, stiffness, limit_min, limit_max) {
        const limited_after_input = clamp(x + delta, limit_min, limit_max);
        if (limited_after_input === x + delta) {
            return x + delta;
        }
        if ((limited_after_input - (x + delta)) * delta >= 0.0) {
            // The input is going away from the limit, so don't limit the sensitivity.
            return x + delta;
        }
        else if (clamp(x, limit_min, limit_max) !== x) {
            // We're outside of our limited range and we're moving further away
            if (x >= limit_max) {
                return x - delta * Math.exp(stiffness * (limit_max - x)) / -stiffness;
            }
            else {
                return x + delta * Math.exp(stiffness * (x - limit_min)) / stiffness;
            }
        }
        else {
            // We're not limited yet, but might be
            // Add the input like normal up until the point we hit the limit
            const input_used_up = Math.sign(delta) * Math.abs(limited_after_input - clamp(x, limit_min, limit_max));
            const input_left_after_limit = delta - input_used_up;
            // Update state
            x = limited_after_input;
            delta = input_left_after_limit;
            // Integrate the input with the springiness for the portion after the limit was exceeded.
            if (x >= limit_max) {
                return x - delta * Math.exp(stiffness * (limit_max - x)) / -stiffness;
            }
            else {
                return x + delta * Math.exp(stiffness * (x - limit_min)) / stiffness;
            }
        }
    }
};
__decorate([
    Deprecated({ new: 'create_debug_geometry' })
], OrbitPlannerCamera.prototype, "generate_debug_geometries", null);
OrbitPlannerCamera = OrbitPlannerCamera_1 = __decorate([
    Debug.Describe(ORBIT_PLANNER_CAMERA_DESCRIPTION)
], OrbitPlannerCamera);
export { OrbitPlannerCamera };
//# sourceMappingURL=OrbitPlannerCamera.js.map